import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast as sonner } from 'sonner'
import { isFocusRoute } from '#app/utils/notification-context.ts'
import { type Toast } from '#app/utils/toast.server.ts'

type PendingToast = { toast: Toast; receivedAt: number }

/**
 * Consume cookie-flashed toasts from the root loader and emit them via Sonner.
 *
 * Focus-route Do-Not-Disturb (ADR-087): while the user is on an immersive
 * practice/performance surface (`isFocusRoute`), non-urgent toasts are held
 * in a ref and replayed once the user navigates away. Urgent toasts always
 * show.
 */
export function useToast(toast?: Toast | null) {
	const location = useLocation()
	const navigate = useNavigate()
	const pendingRef = useRef<PendingToast[]>([])

	useEffect(() => {
		if (!toast) return
		setTimeout(() => {
			const focused = isFocusRoute(location.pathname)
			const urgent = toast.type === 'urgent'
			if (focused && !urgent) {
				pendingRef.current.push({ toast, receivedAt: Date.now() })
				return
			}
			showToast(toast, navigate)
		}, 0)
	}, [toast, location.pathname, navigate])

	// Replay held toasts when the user leaves a focus route.
	useEffect(() => {
		if (isFocusRoute(location.pathname)) return
		if (pendingRef.current.length === 0) return
		const pending = pendingRef.current
		pendingRef.current = []
		for (const { toast } of pending) {
			showToast(toast, navigate)
		}
	}, [location.pathname, navigate])
}

function showToast(toast: Toast, navigate: (to: string) => void): void {
	const title = toast.title
	const options = {
		id: toast.id,
		description: toast.description,
		action: toast.action
			? {
					label: toast.action.label,
					onClick: () => navigate(toast.action!.href),
				}
			: undefined,
		duration:
			toast.type === 'achievement'
				? 6000
				: toast.type === 'urgent'
					? Infinity
					: undefined,
		className:
			toast.type === 'achievement'
				? 'toast-achievement'
				: toast.type === 'urgent'
					? 'toast-urgent'
					: undefined,
	}

	switch (toast.type) {
		case 'success':
		case 'achievement':
			return void sonner.success(title, options)
		case 'error':
		case 'urgent':
			return void sonner.error(title, options)
		case 'info':
			return void sonner.info(title, options)
		case 'warning':
			return void sonner.warning(title, options)
		case 'message':
		default:
			return void sonner(title, options)
	}
}
